import {FormEvent} from 'react'
import {useSelector} from "react-redux";
import {State, store} from '@App/state'
import {useDates} from "@App/hooks/useDates";
import {useLazyQuery, useMutation} from "@apollo/client";
import {CREATE_INQUIRY, CreateInquiryQuery} from "@App/graphql/mutations/createInquiry";
import {useNumberPlate} from "@App/hooks/useNumberPlate";
import {GET_NUMBERPLATE_CHECK} from "@App/graphql/queries";
import {useContext} from "@App/hooks/useContext";
import {PremiumServiceIsRentalCar} from "@App/utils/conditionals/services";
import {OrganizationIsAyvens} from "@App/utils/conditionals/organization";


// used for old google analytics tracking events
declare global {
    const ga: any
    const dataLayer: any
    const plausible: any
}

export const useCreateInquiry = ({
                                     salutation,
                                     firstName,
                                     lastName,
                                     email,
                                     phone,
                                     street,
                                     city,
                                     postcode,
                                     addition,
                                     tos,
                                     marketing,
                                     adacMember,
                                     message,
                                     numberPlate,
                                     mileage
                                 }) => {

    const organization = useSelector((state: State) => state.orders.organization)
    const service = useSelector((state: State) => state.orders.service)
    const garage = useSelector((state: State) => state.orders.garage)
    const premiumServices = useSelector((state: State) => state.orders.premiumServices)
    const slot = useSelector((state: State) => state.orders.slot)
    const {formatNumberPlate} = useNumberPlate()

    const motorization = useSelector((state: State) => state.orders.motorization)
    const gearbox = useSelector((state: State) => state.orders.gearbox)
    const body = useSelector((state: State) => state.orders.body)
    const initialRegistrationDate = useSelector((state: State) => state.orders.initialRegistrationDate)
    const subRepairs = useSelector((state: State) => state.orders.subRepairs)

    const rentalCarSelection = useSelector((state: State) => state.orders.rentalCarSelection)
    const serviceWithAdditionalInformation = OrganizationIsAyvens(organization) ? premiumServices.find(premiumService => PremiumServiceIsRentalCar(premiumService)) : null

    const [createInquiry, {loading, data, error}] = useMutation<CreateInquiryQuery>(CREATE_INQUIRY)

    const {formatDate, toIsoString} = useDates()

    const [checkNumberPlate] = useLazyQuery(GET_NUMBERPLATE_CHECK,{
        variables: {
            organization: organization.id,
            numberPlate: formatNumberPlate(numberPlate),
            service: service?.id
        }
    })

    const {
        getContext
    } = useContext()

    const submit = (e: FormEvent) => {
        e.preventDefault()
        createInquiry({
            variables: {
                organization: organization.id,
                services: service ? [service.id] : null,
                service: service ? service.id : null,
                garage: garage.id,
                state: JSON.stringify(store.getState()),
                firstName: firstName,
                lastName: lastName,
                email: email,
                phone: phone.country + parseInt(phone.number),
                street: street,
                city: city,
                postcode: postcode,
                addition: addition,
                numberPlate: formatNumberPlate(numberPlate),
                mileage: parseInt(mileage),
                message: message,
                tos: tos,
                slot: slot ? slot.id : null,
                marketing: marketing,
                adacMember: adacMember,
                startDate: toIsoString(slot.starts_at),
                endDate: toIsoString(slot.ends_at),
                calendarId: slot.calendar_id,
                salutation: salutation,
                premiumServices: premiumServices.map(premiumService => typeof premiumService.id == 'string' ? parseInt(premiumService.id) : premiumService.id),
                motorization: motorization ? motorization.id : null,
                gearbox: gearbox ? gearbox.id : null,
                body: body ? body.id : null,
                subRepairs: subRepairs ? subRepairs : null,
                registrationDate: initialRegistrationDate
                    ? formatDate(initialRegistrationDate, 'yyyy-MM-dd')
                    : null,
                additionalPremiumServiceInformation: serviceWithAdditionalInformation && rentalCarSelection ? [{id: serviceWithAdditionalInformation.id, additionalInformation: [{rentalCarCategory: rentalCarSelection}]}] : null
            },
            context: getContext(garage)
        }).then((response) => {
            if (typeof dataLayer !== 'undefined') {
                dataLayer.push({
                    'event': 'appointment booked',
                    'bookingDetails': {
                        transactionId: response.data.createInquiry.id,
                        transactionUuid: response.data.createInquiry.uuid,
                        organization: organization.id,
                        service: service.id,
                        serviceUuid: service.uuid,
                        garage: garage.id,
                        garageUuid: garage.uuid,
                        garageName: garage.name,
                        state: JSON.stringify(store.getState()),
                        firstName: firstName,
                        lastName: lastName,
                        email: email,
                        phone: phone.country + parseInt(phone.number),
                        numberPlate: formatNumberPlate(numberPlate),
                        mileage: parseInt(mileage),
                        message: message,
                        tos: tos,
                        slot: slot ? slot.id : null,
                        startDate: toIsoString(slot.starts_at),
                        endDate: toIsoString(slot.ends_at),
                        calendarId: slot.calendar_id,
                        salutation: salutation,
                        premiumServices: premiumServices.map(premiumService => typeof premiumService.id == 'string' ? parseInt(premiumService.id) : premiumService.id),
                        motorization: motorization ? motorization.id : null,
                        gearbox: gearbox ? gearbox.id : null,
                        body: body ? body.id : null,
                        subRepairs: subRepairs ? subRepairs : null,
                        registrationDate: initialRegistrationDate
                            ? formatDate(initialRegistrationDate, 'yyyy-MM-dd')
                            : null,
                        additionalPremiumServiceInformation: serviceWithAdditionalInformation && rentalCarSelection ? [{id: serviceWithAdditionalInformation.id, additionalInformation: [{rentalCarCategory: rentalCarSelection}]}] : null
                    }
                });
            }
            if (typeof plausible !== 'undefined') {
                plausible('appointment booked', {
                    props: {
                        organization: organization.id,
                        service: service.id,
                        garage: garage.id,
                        garageName: garage.name,
                        premiumServices: premiumServices.map(premiumService => typeof premiumService.id == 'string' ? parseInt(premiumService.id) : premiumService.id),
                        subRepairs: subRepairs ? subRepairs : null
                    }
                })
            }
        })
    }

    const hasValidationError = (value: string): boolean => {
        return error.graphQLErrors.some(error => error.message.includes(value)
            || error.extensions.validation && 'input.' + value.replace('$', '') in error.extensions.validation)
    }

    return {
        checkNumberPlate,
        organization,
        service,
        garage,
        premiumServices,
        slot,
        error,
        loading,
        inquiry: data ? data.createInquiry : null,
        validationErrors: {
            ...error && hasValidationError('$firstName') && {firstName: true},
            ...error && hasValidationError('$lastName') && {lastName: true},
            ...error && hasValidationError('$email') && {email: true},
            ...error && hasValidationError('$phone') && {phone: true},
            ...error && hasValidationError('$numberPlate') && {numberPlate: true},
            ...error && hasValidationError('$mileage') && {mileage: true},
            ...error && hasValidationError('$message') && {message: true},
            ...error && hasValidationError('$tos') && {tos: true},
        },
        submit
    }
}
